/**
 * @fileoverview Helper properties and functions for fabric-canvas component.
 *
 * @author Louie Tian <louie.tian@logicsolutions.com>
 * @date August 14, 2024
 * @version 1.0.0
 *
 * This file includes types, enums and functions used directly or indirectly by fabric-canvas component.
 */

import { fabric } from 'fabric';
import { HeatmapRenderingProps } from './fabric-extension';

export const CanvasWidth = 1024;
export const CanvasHeight = 576;

// Type definition for shape options
export type HeatMapObjectOptions = fabric.IImageOptions & fabric.IRectOptions & fabric.ICircleOptions & fabric.IPolylineOptions & HeatmapRenderingProps;
export type HeatMapRectOptions = fabric.IRectOptions & HeatmapRenderingProps;
export type HeatMapCircleOptions = fabric.ICircleOptions & HeatmapRenderingProps;
export type HeatMapPolygonOptions = fabric.IPolylineOptions & HeatmapRenderingProps;

// Type definition for objects
export type HeatmapObject = fabric.Image & fabric.Rect & fabric.Circle & fabric.Polygon & HeatmapRenderingProps;
export type HeatmapImage = fabric.Image & HeatmapRenderingProps;
export type HeatmapRect = fabric.Rect & HeatmapRenderingProps;
export type HeatmapCircle = fabric.Circle & HeatmapRenderingProps;
export type HeatmapPolygon = fabric.Polygon & HeatmapRenderingProps;
export type HeatmapPoint = { x: number, y: number };

// Enum for type of shape for drawn items
export enum ShapeType {
  Rectangle = 'Rect',
  Circle = 'Circle',
  Polygon = 'Polygon',
  Icon = 'Icon',
  Select = 'select'
}

// Enum for action mode on canvas
export enum ActionMode {
  Drawing = 'Drawing',
  Selection = 'Selection',
  View = 'View'
}

// Enum for action mode that displays to user
export enum DisplayActionMode {
  RectDrawing = 'RectDrawing',
  CircleDrawing = 'CircleDrawing',
  PolygonDrawing = 'PolygonDrawing',
  IconDrawing = 'IconDrawing',
  Selection = 'Selection',
  View = 'View'
}

// Enum for type of icon
export enum IconType {
  ContractorWork = 'ContractorWork',
  RiskAssessment = 'RiskAssessment',
  Inspection = 'Inspection',
  BBSO = 'BBSO',
  Incident = 'Incident',
  GMPAudit = 'GMPAudit',
  LocationPoint = 'LocationPoint'
}

// Mapping between icon type and corresponding icon file path
export const IconPath: { [iconType in IconType]: string } = {
  [IconType.ContractorWork]: 'assets/media/icons/contractor-work.svg',
  [IconType.RiskAssessment]: 'assets/media/icons/exclamation-alert.svg',
  [IconType.Inspection]: 'assets/media/icons/inspection.svg',
  [IconType.BBSO]: 'assets/media/icons/observation.svg',
  [IconType.Incident]: 'assets/media/icons/accident.svg',
  [IconType.GMPAudit]: 'assets/media/icons/audit.svg',
  [IconType.LocationPoint]: 'assets/media/icons/location-point.svg'
}

// Enum for type of file being loaded using Fabric.js
export enum FileType {
  PDF = 'pdf',
  JPG = 'jpg',
  JPEG = 'jpeg',
  PNG = 'png',
  SVG = 'svg'
}

// List of all properties for general objects as well as shape-specific objects
export const ObjectRenderingProperty = {
  General: [
    'aCoords',
    'oCoords',
    'left',
    'top',
    'stroke',
    'strokeWidth',
    'fill',
    'scaleX',
    'scaleY',
    'angle',
    'locationId',
    'locationName',
    'objectId',
    'objectType',
    'objectName',
    'tooltipText',
    'riskLevel',
    'iconType',
    'titleText',
    'length',
    'contractorWorkCount',
    'highRiskCount',
    'personnelCount',
    'subIcons',
  ],
  [ShapeType.Rectangle]: [
    'width',
    'height',
  ],
  [ShapeType.Circle]: [
    'radius',
    'originX',
    'originY',
  ],
  [ShapeType.Polygon]: [
    'points',
  ],
  [ShapeType.Icon]: [
    'scaleX',
    'scaleY',
    'originX',
    'originY',
    'sourcePath',
    'fileType'
  ]
};

// Enum for location type
export enum LocationType {
  Area = '_SYS_SPACE_TYPE_0',
  Building = '_SYS_SPACE_TYPE_1',
  Point = '_SYS_SPACE_TYPE_2'
}

// Enum for interactive mode of this canvas
export enum InteractiveMode {
  View = 'view',
  Edit = 'edit'
}

// Enum for risk level for the shape
export enum RiskLevel {
  NA = '_SYS_RR_0',
  ExLow = '_SYS_RR_1',
  Low = '_SYS_RR_2',
  Medium = '_SYS_RR_3',
  High = '_SYS_RR_4',
  ExHigh = '_SYS_RR_5'
}

// Enum for risk level corresponding fill color for the shape
const RiskColorMapping: { [key in RiskLevel]: string } = {
  [RiskLevel.NA]: 'rgb(0, 100, 255)',
  [RiskLevel.ExLow]: 'rgb(0, 255, 0)',
  [RiskLevel.Low]: 'rgb(115, 201, 48)',
  [RiskLevel.Medium]: 'rgb(241, 236, 0)',
  [RiskLevel.High]: 'rgb(255, 180, 0)',
  [RiskLevel.ExHigh]: 'rgb(255, 0, 0)'
};

// Function to get the color with a specific alpha value
const getColorWithAlpha = (baseColor: string, alpha: number): string =>
  `${baseColor.replace('rgb', 'rgba').replace(')', `, ${alpha})`)}`;

// Function to get the fill color
export const getFillColor = (riskLevel: RiskLevel): string => {
  return getColorWithAlpha(RiskColorMapping[riskLevel], 0.25);
}

// Function to get the stroke color
export const getStrokeColor = (riskLevel: RiskLevel): string => {
  return getColorWithAlpha(RiskColorMapping[riskLevel], 1);
}
